import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import Dropdown from "react-bootstrap/Dropdown";
import { FiActivity } from "react-icons/fi";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from "chart.js";
import { getFetchSymtoms } from "../services/analyticsService";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

const SymptomTrendsChart = ({ data }) => {
  const [timeRange, setTimeRange] = useState("week"); // "day", "week", "month"

  const [graphData, setGraphData] = useState({ labels: [], values: [] });
  const [isLoading, setIsLoading] = useState(true);

  const [selectedCategory, setSelectedCategory] = useState("Urinary Frequency");

  const handleTabClick = (range) => {
    setTimeRange(range);
  };

  useEffect(() => {
    let isCurrentRequest = true;

    const fetchSymptoms = async () => {
      setIsLoading(true);
      try {
        const response = await getFetchSymtoms({
          patientId: data?._id,
          category: selectedCategory,
          time: timeRange,
          web: true,
        });
        if (isCurrentRequest) {
          setGraphData({
            labels: Array.isArray(response?.data?.labels) ? response.data.labels : [],
            values: Array.isArray(response?.data?.values) ? response.data.values : [],
          });
        }
      } finally {
        if (isCurrentRequest) setIsLoading(false);
      }
    };

    fetchSymptoms();
    return () => {
      isCurrentRequest = false;
    };
  }, [timeRange, data?._id, selectedCategory]);

  const handleCategory = (eventKey) => {
    setSelectedCategory(eventKey); // Update the state with the selected value
    // _getForumTopicList(eventKey);
  };

  const chartData = {
    labels: graphData.labels,
    datasets: [
      {
        label: `${selectedCategory} Trend`,
        data: graphData.values,
        borderColor: "rgba(75, 192, 192, 1)",
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        tension: 0.3, // Smooth curve
        pointRadius: 7,
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 30, // Increase space at the top
      },
    },
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          usePointStyle: true,
          boxWidth: 10,
        },
      },
      datalabels: {
        display: function (context) {
          // Hide datalabels if the selected category is "Sleep Quality"
          return selectedCategory !== "Sleep Quality";
        },
        color: "#333",
        anchor: "end",
        align: "top",
        font: {
          size: 12,
          weight: "bold",
        },
      },

      tooltip: {
        callbacks: {
          label: function (context) {
            const value = context.raw;
            const sleepLabels = {
              0: "Not at all",
              1: "Somewhat",
              2: "Yes, a lot",
            };

            if (selectedCategory === "Sleep Quality") {
              return `${selectedCategory}: ${sleepLabels[value] ?? value}`;
            } else {
              return `${selectedCategory}: ${value}`;
            }
          },
        },
      },
    },

    scales: {
      x: {
        title: {
          display: true,
          text: "Time",
          font: {
            size: 14,
          },
        },
      },
      y: {
        beginAtZero: true,
        // Always go up to 3
        ...(selectedCategory === "Sleep Quality" && {
          // min: 0,       // Always start from 0
          ticks: {
            stepSize: 1,
            callback: function (value) {
              const labels = {
                0: "Not at all",
                1: "Somewhat",
                2: "Yes, a lot",
              };
              return labels[value] ?? value;
            },
          },
        }),
        // suggestedMax: dynamicMax,
        title: {
          display: true,
          text: `${selectedCategory}${selectedCategory === "Adherence" ? "%" : ""
            } Values`,
          font: {
            size: 14,
          },
        },
      },
    },
  };

  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        <Dropdown className="graph-btns tabs mx-2 symptom-trends-dropdown" onSelect={handleCategory}>
          <Dropdown.Toggle className="db-dropdown  h-100">
            {selectedCategory === ""
              ? "category"
              : selectedCategory === "All-Record"
                ? "All Forum"
                : selectedCategory}
          </Dropdown.Toggle>

          <Dropdown.Menu>
            <Dropdown.Item eventKey="Urinary Frequency">
              Urinary Frequency
            </Dropdown.Item>

            <Dropdown.Item eventKey="Urgency">Urgency</Dropdown.Item>

            <Dropdown.Item eventKey="Pain Levels">Pain Levels</Dropdown.Item>

            <Dropdown.Item eventKey="Sleep Quality">
              Sleep Quality
            </Dropdown.Item>
            <Dropdown.Item eventKey="Adherence">Adherence</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        <div className="graph-btns tabs">
          <button
            onClick={() => handleTabClick("week")}
            className={timeRange === "week" ? "active" : ""}
          >
            Week
          </button>
          <button
            onClick={() => handleTabClick("month")}
            className={timeRange === "month" ? "active" : ""}
          >
            Month
          </button>
          <button
            onClick={() => handleTabClick("year")}
            className={timeRange === "year" ? "active" : ""}
          >
            Year
          </button>
        </div>
      </div>
      <div className="symptom-chart-canvas" aria-live="polite">
        {isLoading ? (
          <div className="symptom-chart-empty">
            <span className="spinner-border spinner-border-sm" aria-hidden="true" />
            <strong>Loading symptom trends</strong>
          </div>
        ) : graphData.values.length > 0 ? (
          <Line data={chartData} options={options} />
        ) : (
          <div className="symptom-chart-empty">
            <FiActivity aria-hidden="true" />
            <strong>No trend data available</strong>
            <span>Try another category or time range.</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SymptomTrendsChart;
